import deviceApi from '@api/deviceApi';
import { ActionButton } from '@components/actionButton';
import Blocker from '@components/blocker';
import DefaultPage from '@components/defaultPage';
import ImageInput from '@components/imageInput';
import MapPositionInput from '@components/mapPositionInput';
import { useTranslator } from '@i18n';
import { Textarea } from '@mantine/core';
import type { DeviceDraft, DeviceWithLocation } from '@models/device';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useHandleError } from '@util/errors';
import { createForm } from 'cross-state/react';

export const Route = createFileRoute('/inventory/$deviceType/$deviceId/edit')({
  component: EditDevicePage,
});

const form = createForm<DeviceDraft>({
  defaultValue: {
    id: '',
    type: '',
    location: {
      locationId: '',
      sequenceNumber: 0,
      description: '',
    },
    states: [],
    createdOn: new Date().toISOString(),
    modifiedOn: new Date().toISOString(),
    history: [],
  },
  validations: {
    'location.description': {
      required: (x) => !!x,
    },
  },
});

const { Form, Field, FormState, useForm } = form;

function EditDevicePage() {
  const t = useTranslator();
  const { locationId = '' } = Route.useSearch();
  const { deviceId } = Route.useParams();
  const [device, deviceError] = deviceApi.get(locationId, deviceId).useCache({ loadingBoundary: true });
  useHandleError(deviceError);

  if (!device) {
    return null;
  }

  return (
    <Form original={device} localizeError={() => t('validation.required')}>
      <Content device={device} />
      <Blocker form={form} />
    </Form>
  );
}

function Content({ device }: { device: DeviceWithLocation }) {
  const t = useTranslator();
  const { deviceType, deviceId } = Route.useParams();
  const { locationId = '' } = Route.useSearch();
  const form = useForm();
  const navigate = useNavigate();

  async function save() {
    if (!form.validate()) {
      return;
    }

    const { id, location, v } = form.getDraft();
    await deviceApi.updateInLocation(deviceType, id, location);
    await deviceApi.whenUpdated(locationId, id, v);
    form.reset();

    setTimeout(() => {
      navigate({
        to: '/inventory/$deviceType/$deviceId',
        params: { deviceType, deviceId },
      });
    });
  }

  return (
    <DefaultPage
      backButton={{ to: '..' }}
      title={[
        t.dynamic(`entities.${device.type}`, {}, { fallback: device.type }),
        String(device.location.sequenceNumber).padStart(4, '0'),
      ].join(' ')}
      css={{
        display: 'grid',
        gap: 'var(--mantine-spacing-md)',
        alignContent: 'start',
      }}
    >
      <Field
        name="location.description"
        render={(props) => (
          <Textarea
            {...props}
            value={props.value ?? ''}
            label={t('device.properties.description.label')}
            withAsterisk
            description={t('device.properties.description.description')}
            autosize
            minRows={2}
          />
        )}
      />

      <Field
        name="location"
        render={(props) => (
          <MapPositionInput
            {...props}
            value={props.value ?? null}
            device={device}
            label={t('device.properties.map.label')}
            description={t('device.properties.map.description')}
          />
        )}
      />

      <Field
        name="location.photo"
        render={(props) => (
          <ImageInput
            {...props}
            value={props.value ?? null}
            label={t('device.properties.photo.label')}
            description={t('device.properties.photo.description')}
          />
        )}
      />

      <FormState selector={(state) => state.hasChanges}>
        {(hasChanges) => (
          <ActionButton type="submit" sticky disabled={!hasChanges} onClick={save}>
            {t('common.save')}
          </ActionButton>
        )}
      </FormState>
    </DefaultPage>
  );
}
